import { useState } from 'react';
import { 
    Plus, 
    Minus,
    X,
    Search,
    Dumbbell,
    Hash,
    Clock,
    Weight
} from 'lucide-react';
import Button from '@mui/material/Button';

type AddConditioningExerciseProps = {
    exerciseList: any[];
    handleSubmit: (exercise: any) => void;
    handleCancel: () => void;
};

const AddConditioningExercise: React.FC<AddConditioningExerciseProps> = ({ 
    exerciseList, 
    handleSubmit, 
    handleCancel 
}) => {
    const [exercise, setExercise] = useState({
        exercise_id: 1,
        exercise_type: 'standard',
        reps: 1,
        duration: 0,
        weight: 0
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    const filteredExercises = exerciseList.filter(ex => 
        ex.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const currentExercise = exerciseList.find(ex => 
        ex.id === exercise.exercise_id && ex.type === exercise.exercise_type
    );

    const handleExerciseSelect = (selectedExercise: any) => {
        setExercise({
            ...exercise,
            exercise_id: selectedExercise.id,
            exercise_type: selectedExercise.type
        });
        setSearchTerm(selectedExercise.name);
        setShowDropdown(false);
    };

    const handleRepsChange = (newReps: number) => {
        setExercise({
            ...exercise,
            reps: Math.max(1, newReps)
        });
    };

    const handleFloatChange = (fieldName: string, value: string) => {
        let newVal = parseFloat(value.trim());
        newVal = isNaN(newVal) ? 0 : Math.max(0, newVal);
        
        setExercise(prev => ({ ...prev, [fieldName]: newVal }));
    };

    const preSubmit = () => {
        const finalEx = {
            ...exercise,
            duration: parseFloat(String(exercise.duration)),
            weight: parseFloat(String(exercise.weight))
        };
        handleSubmit(finalEx);
    };

    return (
        <div className="fixed inset-0 bg-black-500 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-6 text-black relative">
                    <div className="absolute inset-0 bg-white opacity-10"></div>
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center space-x-3  text-white">
                            <Plus className="w-6 h-6" />
                            <h2 className="text-xl font-bold">Add Exercise</h2>
                        </div>
                        <button
                            onClick={handleCancel}
                            className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Exercise Selection */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Exercise</label>
                        <div className="relative">
                            <div className="relative">
                                <Search className="w-5 h-5 text-gray-400 absolute left-1 top-1/2 transform -translate-y-1/2" />
                                <input
                                    type="text"
                                    value={searchTerm || currentExercise?.name || ''}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setShowDropdown(true);
                                    }}
                                    onFocus={() => setShowDropdown(true)}
                                    placeholder="Search or select an exercise"
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                                    style={{ fontSize: '1rem' }}
                                />
                            </div>
                            
                            {showDropdown && filteredExercises.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {filteredExercises.map((ex) => (
                                        <button
                                            key={`${ex.id}-${ex.type}`}
                                            onClick={() => handleExerciseSelect(ex)}
                                            className="w-full px-4 py-3 text-left hover:bg-emerald-50 focus:bg-emerald-50 outline-none border-b border-gray-100 last:border-b-0"
                                        >
                                            <div className="flex items-center space-x-2">
                                                <Dumbbell className="w-4 h-4 text-emerald-500" />
                                                <span style={{ fontSize: '1rem' }}>{ex.name}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Reps Section */}
                    <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-3">
                            <Hash className="w-5 h-5 text-blue-500" />
                            <span className="text-base font-medium text-blue-800">Reps</span>
                        </div>
                        
                        <div className="flex items-center justify-center space-x-4 mb-3">
                            <button
                                onClick={() => handleRepsChange(exercise.reps - 1)}
                                className="w-10 h-10 bg-blue-200 hover:bg-blue-300 rounded-full flex items-center justify-center transition-colors"
                            >
                                <Minus className="w-4 h-4 text-blue-700" />
                            </button>
                            <span className="text-2xl font-bold text-blue-800 min-w-[3rem] text-center">
                                {exercise.reps}
                            </span>
                            <button
                                onClick={() => handleRepsChange(exercise.reps + 1)}
                                className="w-10 h-10 bg-blue-200 hover:bg-blue-300 rounded-full flex items-center justify-center transition-colors"
                            >
                                <Plus className="w-4 h-4 text-blue-700" />
                            </button>
                        </div>

                        <div className="flex justify-center space-x-2">
                            <button
                                onClick={() => handleRepsChange(exercise.reps + 5)}
                                className="px-3 py-1 bg-blue-200 hover:bg-blue-300 text-blue-700 rounded-lg font-medium transition-colors"
                            >
                                <span style={{ fontSize: '1rem' }}>+5</span>
                            </button>
                            <button
                                onClick={() => handleRepsChange(exercise.reps + 10)}
                                className="px-3 py-1 bg-blue-200 hover:bg-blue-300 text-blue-700 rounded-lg font-medium transition-colors"
                            >
                                <span style={{ fontSize: '1rem' }}>+10</span>
                            </button>
                            <button
                                onClick={() => handleRepsChange(1)}
                                className="px-3 py-1 bg-blue-200 hover:bg-blue-300 text-blue-700 rounded-lg font-medium transition-colors"
                            >
                                <span style={{ fontSize: '1rem' }}>Reset</span>
                            </button>
                        </div>
                    </div>

                    {/* Weight Input */}
                    <div className="space-y-2">
                        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                            <Weight className="w-4 h-4" />
                            <span>Weight (kg)</span>
                        </label>
                        <input
                            type="number"
                            value={exercise.weight}
                            onChange={(e) => setExercise(prev => ({ ...prev, weight: Number(e.target.value) }))}
                            onBlur={(e) => handleFloatChange('weight', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                            style={{ fontSize: '1rem' }}
                            placeholder="0"
                        />
                    </div>

                    {/* Duration Input (only show for single rep) */}
                    {exercise.reps === 1 && (
                        <div className="space-y-2">
                            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                                <Clock className="w-4 h-4" />
                                <span>Duration (seconds)</span>
                            </label>
                            <input
                                type="number"
                                value={exercise.duration}
                                onChange={(e) => setExercise(prev => ({ ...prev, duration: Number(e.target.value) }))}
                                onBlur={(e) => handleFloatChange('duration', e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                                style={{ fontSize: '1rem' }}
                                placeholder="0"
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className='bottom-buttons'>
                    <Button
                        style={{margin: "15px 5px 5px", width: '100px'}}
                        onClick={preSubmit}
                        variant="contained"
                    >
                        Add
                    </Button>
    
                    <Button
                        style={{margin: "15px 5px 5px", width: '100px'}}
                        onClick={handleCancel}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AddConditioningExercise;